import type { ConfirmationResult, RecaptchaVerifier } from "firebase/auth";
import { I18n } from "@assets/strings";
import React, { useEffect, useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import styled, { css } from "styled-components";
import { useServices } from "@core/servicesContext";
import { CountryIndicator } from "@domain/currentProfile/countriesIndicators";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { useAsync } from "@utils/hooks";
import { LoadingButton } from "@ui/components/buttons/loadingButton";
import { ErrorContainer } from "@ui/components/containers/errorContainer";
import { PrimaryMedium } from "@ui/components/containers/primary";
import { PhoneNumberIcon } from "@ui/components/icons";
import { useForm } from "@ui/components/inputs/hooks";
import { IndicatorDataList } from "@ui/components/inputs/indicatorDataList";
import { SimpleTextInput } from "@ui/components/inputs/simpleTextInput";
import { Row } from "@ui/components/layout";
import { theme } from "@ui/styles/theme";
import { gap } from "@ui/styles/utils";
import { createToast } from "@ui/toasts/utils";

export const LinkNumberForm: React.FC = ({ ...htmlProps }) => {
	const {
		checkSubmit: checkPhoneNumberSubmit,
		fieldProps: phoneNumberFieldProps,
		formValue: phoneNumberFormValue,
	} = useForm({ phoneNumber: "" }, ["phoneNumber"], {
		phoneNumber: (p) => p.length >= 1,
	});
	const {
		checkSubmit: checkOtpSubmit,
		fieldProps: OtpFieldProps,
		formValue: otpFormValue,
	} = useForm({ otpCode: "" }, ["otpCode"], {
		otpCode: (p) => p.length >= 6,
	});

	// const recaptchaRef = useRef<any>(null);
	const [indicator, setIndicator] = useState<CountryIndicator>("221");
	const [otpStep, setOptStep] = useState<boolean>(false);
	const [confirm, setConfirm] = useState<ConfirmationResult>();

	const { currentProfileService } = useServices();
	const [seconds, setSeconds] = useState(30);
	const [count, setCount] = useState<number>(3);
	const [isCodeSent, setIsCodeSent] = useState(false);
	const currentProfile = useCurrentProfile();

	const {
		run: sendVerificationCode,
		loading,
		error,
	} = useAsync(async (): Promise<RecaptchaVerifier | undefined> => {
		try {
			const setup = await currentProfileService.setupRecaptcha(`+${indicator}${phoneNumberFormValue.phoneNumber}`);
			setConfirm(confirm);
			setOptStep(true);
			setIsCodeSent(true);
			createToast(I18n.t("success"), I18n.t("settings.link_phonenumber.toast.success.message"), "success");
			return setup?.recaptcha;
		} catch (error) {
			console.log("error", error);
			createToast(I18n.t("error"), I18n.t("settings.link_phonenumber.toast.error.message"), "error");
			return;
		}
	}, [indicator, phoneNumberFormValue.phoneNumber]);

	const { run: linkPhoneNumber } = useAsync(
		async () => currentProfileService.linkPhoneNumber(confirm as ConfirmationResult, otpFormValue.otpCode),
		[otpFormValue.otpCode, confirm]
	);

	const { run: updatePhoneNumber } = useAsync(
		async () =>
			currentProfileService.updatePhoneNumber(
				confirm as ConfirmationResult,
				otpFormValue.otpCode,
				indicator,
				phoneNumberFormValue.phoneNumber
			),
		[confirm, otpFormValue.otpCode, indicator, phoneNumberFormValue.phoneNumber]
	);

	useEffect(() => {
		const interval = setInterval(() => {
			if (seconds > 0 && isCodeSent) {
				setSeconds(seconds - 1);
			}

			if (seconds === 0) {
				setIsCodeSent(false);
				clearInterval(interval);
				// console.log("recaptcha", recaptchaRef.current);
			}
		}, 1000);

		return () => {
			clearInterval(interval);
		};
	}, [seconds, isCodeSent]);

	const history = useHistory();

	return (
		<>
			{!otpStep ? (
				<Container
					{...htmlProps}
					onSubmit={async (e) => {
						e.preventDefault();
						if (!checkPhoneNumberSubmit()) {
							return;
						}
						await sendVerificationCode();
						// if (recaptcha) recaptcha.clear();
					}}
				>
					{currentProfile?.phoneNumberAuth ? (
						<Title>{I18n.t("settings.update_phonenumber.title")}</Title>
					) : (
						<Title>{I18n.t("settings.link_phonenumber.title")}</Title>
					)}
					<Fields>
						<FieldWrapper>
							{currentProfile?.phoneNumberAuth ? (
								<label htmlFor="link-phone">{I18n.t("settings.update_phonenumber.label")}</label>
							) : (
								<label htmlFor="link-phone">{I18n.t("settings.link_phonenumber.label")}</label>
							)}
							<Row align="center" gap={10}>
								<IndicatorDataList value={indicator} onChange={(value) => setIndicator(value)} />
								<SimpleTextInput
									id="link-phone"
									color="primary"
									icon={<PhoneNumberIcon color={theme.colors.primary} />}
									iconPosition="left"
									autoComplete="phone-number"
									{...phoneNumberFieldProps("phoneNumber")}
									placeholder={I18n.t("register.phonenumber.placeholder")}
								/>
							</Row>
						</FieldWrapper>
						{error && <ErrorContainer>{I18n.t("auth.error.default")}</ErrorContainer>}
					</Fields>
					<div id="recaptcha-container" />
					<Row gap={10}>
						<CancelButton onClick={() => history.goBack()}>{I18n.t("cancel")}</CancelButton>
						<LoadingButton loading={loading} type="submit">
							{I18n.t("validate")}
						</LoadingButton>
					</Row>
				</Container>
			) : (
				<Container
					{...htmlProps}
					onSubmit={async (e) => {
						e.preventDefault();
						if (!checkOtpSubmit() || !confirm) {
							return;
						}
						try {
							if (currentProfile?.phoneNumberAuth) {
								await updatePhoneNumber();
							} else {
								await linkPhoneNumber();
							}
							history.push("/");
							createToast(I18n.t("success"), I18n.t("settings.link_phonenumber.otp.toast.success.message"), "success");
						} catch {
							createToast(I18n.t("error"), I18n.t("settings.link_phonenumber.otp.toast.error.message"), "error");
						}
					}}
				>
					<Title>{I18n.t("settings.link_phonenumber.otp.title")}</Title>
					<Row gap={5}>
						<StyledPrimaryMedium>{I18n.t("settings.link_phonenumber.otp.message")}</StyledPrimaryMedium>
						<StyledPrimaryMedium colored>
							+{indicator}
							{phoneNumberFormValue.phoneNumber}
						</StyledPrimaryMedium>
					</Row>
					<Fields>
						<FieldWrapper>
							<label htmlFor="link-otp">{I18n.t("settings.link_phonenumber.otp.label")}</label>
							<SimpleTextInput id="link-otp" autoComplete="otp-code" {...OtpFieldProps("otpCode")} />
						</FieldWrapper>
						{error && <ErrorContainer>{I18n.t("auth.error.default")}</ErrorContainer>}
					</Fields>
					<div id="recaptcha-container-resend" />
					<Row gap={20}>
						<CancelButton onClick={() => setOptStep(false)}>{I18n.t("cancel")}</CancelButton>
						<LoadingButton type="submit">{I18n.t("validate")}</LoadingButton>
					</Row>

					<Label>
						{I18n.t("code.message")}
						{isCodeSent ? (
							<Counter>00 : {seconds}</Counter>
						) : (
							<StyledTertiaryButton
								disabled={count === 0}
								onClick={async () => {
									setSeconds(15);
									setCount((current) => current - 1);
									setOptStep(!otpStep);
								}}
							>
								{I18n.t("code.resend")}
							</StyledTertiaryButton>
						)}
					</Label>
				</Container>
			)}
		</>
	);
};

const Container = styled.form`
	${gap(54)}
`;

const Title = styled.div`
	${({ theme }) => theme.textStyles.title};
`;

const Fields = styled.div`
	${gap(30)}
`;

const FieldWrapper = styled.div`
	label {
		display: block;
		${({ theme }) => theme.textStyles.primaryMedium};
		margin-bottom: 16px;
	}
`;

const StyledPrimaryMedium = styled(PrimaryMedium).withConfig({
	shouldForwardProp: (prop) => prop !== "colored",
})<{ colored?: boolean }>`
	color: ${({ theme, colored }) => colored && theme.colors.secondary};
	font-size: 10px;
	font-weight: 500;
`;

const CancelButton = styled.div`
	${({ theme }) => theme.textStyles.primaryBold};
	cursor: pointer;
	border: none;
	outline: none;
	border-radius: 6px;
	padding: 12px 40px;
	line-height: 15px;
	background-color: ${({ theme }) => theme.colors.lightergray};
	color: ${({ theme }) => theme.colors.textSecondary};
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: ${({ theme }) => theme.colors.primaryLight};
	color: ${({ theme }) => theme.colors.primary};
	&:hover  {
		background-color: ${({ theme }) => theme.colors.primaryLightTouch};
	}
`;

const StyledTertiaryButton = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>`
	color: ${({ theme }) => theme.colors.secondary};
	cursor: pointer;
	display: block;
	border: none;
	outline: none;
	font-size: 0.9375rem;
	text-decoration: underline;
	border-radius: 13px;
	transition: color 0.1s linear;
	background: none;

	:hover  {
		color: ${({ theme }) => theme.colors.lightgray};
	}

	${({ theme, disabled }) =>
		disabled &&
		css`
			pointer-events: none;
			color: ${theme.colors.lightgray};
			box-shadow: none;
		`}
`;

const Counter = styled.div`
	color: ${({ theme }) => theme.colors.secondary};
	font-style: normal;
	font-weight: 400;
	font-size: 14px;
	line-height: 26px;
`;

const Label = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 5px;
	font-weight: 400;
	font-size: 14px;
	line-height: 26px;
	color: rgba(0, 0, 0, 0.5);
`;
